import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Theme, ThemeInput, ThemeExport } from '../types'
import { useFontLoader } from '../hooks/useFontLoader'
import { generateCSSFile } from '../utils/themeToCSS'
import { ThemeEditor } from '../components/ThemeEditor'
import { LandingPagePreview } from '../previews/LandingPagePreview'
import { AppPreview } from '../previews/AppPreview'

interface ThemeEditorPageProps {
  themes: Theme[]
  activeThemeId: string | null
  prefersDarkMode: boolean
  getThemeBySlug: (slug: string) => Theme | null
  setActiveTheme: (id: string) => void
  updateTheme: (id: string, updates: Partial<ThemeInput>) => void
  deleteTheme: (id: string) => void
  duplicateTheme: (id: string) => Promise<Theme | null>
  exportTheme: (id: string) => ThemeExport | null
  importTheme: (data: ThemeExport) => Promise<Theme>
  toggleDarkMode: () => void
}

export function ThemeEditorPage({
  themes,
  activeThemeId,
  prefersDarkMode,
  getThemeBySlug,
  setActiveTheme,
  updateTheme,
  deleteTheme,
  duplicateTheme,
  exportTheme,
  importTheme,
  toggleDarkMode,
}: ThemeEditorPageProps) {
  const { themeSlug } = useParams<{ themeSlug: string }>()
  const navigate = useNavigate()
  const [previewMode, setPreviewMode] = useState<'landing' | 'app'>('landing')
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)

  const theme = themeSlug ? getThemeBySlug(themeSlug) : null

  // Set as active theme when navigating to it
  useEffect(() => {
    if (theme && theme.id !== activeThemeId) {
      setActiveTheme(theme.id)
    }
  }, [theme, activeThemeId, setActiveTheme])

  // Redirect if theme not found
  useEffect(() => {
    if (themeSlug && !theme && themes.length > 0) {
      window.location.href = '/themes'
    }
  }, [themeSlug, theme, themes.length, navigate])

  // Load fonts
  useFontLoader(theme ? [theme.displayFont, theme.bodyFont] : [])

  // Close menus on outside click
  useEffect(() => {
    function handleClick() {
      setShowExportMenu(false)
      setShowMoreMenu(false)
    }
    if (showExportMenu || showMoreMenu) {
      setTimeout(() => document.addEventListener('click', handleClick), 0)
      return () => document.removeEventListener('click', handleClick)
    }
  }, [showExportMenu, showMoreMenu])

  if (!theme) return null

  const handleUpdate = (updates: Partial<ThemeInput>) => {
    updateTheme(theme.id, updates)
  }

  const handleExportJSON = () => {
    const data = exportTheme(theme.id)
    if (!data) return
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${theme.name.toLowerCase().replace(/\s+/g, '-')}-theme.json`
    a.click()
    URL.revokeObjectURL(url)
    setShowExportMenu(false)
  }

  const handleExportCSS = () => {
    const css = generateCSSFile(theme)
    const blob = new Blob([css], { type: 'text/css' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${theme.name.toLowerCase().replace(/\s+/g, '-')}-theme.css`
    a.click()
    URL.revokeObjectURL(url)
    setShowExportMenu(false)
  }

  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = async (event) => {
        try {
          const data = JSON.parse(event.target?.result as string)
          if (data.version && data.theme) {
            const imported = await importTheme(data)
            navigate(`/themes/${imported.slug}/edit`)
          } else {
            alert('Invalid theme file format')
          }
        } catch {
          alert('Failed to parse theme file')
        }
      }
      reader.readAsText(file)
    }
    input.click()
    setShowMoreMenu(false)
  }

  const handleDelete = () => {
    if (!confirm(`Delete "${theme.name}"? This cannot be undone.`)) return
    deleteTheme(theme.id)
    window.location.href = '/themes'
  }

  const handleDuplicate = async () => {
    const dup = await duplicateTheme(theme.id)
    if (dup) navigate(`/themes/${dup.slug}/edit`)
    setShowMoreMenu(false)
  }

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col bg-gray-100">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white flex-shrink-0">
        {/* Left: back + theme name */}
        <div className="flex items-center gap-2 min-w-0">
          <a
            href="/themes"
            className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
            title="Back to gallery"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Gallery
          </a>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-semibold text-gray-900 truncate">{theme.name}</span>
          {theme.systemTheme && (
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium flex-shrink-0">
              System
            </span>
          )}
        </div>

        {/* Center: preview mode toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setPreviewMode('landing')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              previewMode === 'landing'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Landing Page
          </button>
          <button
            onClick={() => setPreviewMode('app')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              previewMode === 'app'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Web App
          </button>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1.5">
          {/* Dark/Light toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            title={prefersDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {prefersDarkMode ? (
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>

          {/* Export menu */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowExportMenu(!showExportMenu)
                setShowMoreMenu(false)
              }}
              className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
            >
              Export
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  onClick={handleExportJSON}
                  className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 cursor-pointer"
                >
                  Export as JSON
                </button>
                <button
                  onClick={handleExportCSS}
                  className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 cursor-pointer"
                >
                  Export as CSS
                </button>
              </div>
            )}
          </div>

          {/* More actions menu */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowMoreMenu(!showMoreMenu)
                setShowExportMenu(false)
              }}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              title="More actions"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>
            {showMoreMenu && (
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  onClick={handleDuplicate}
                  className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                >
                  <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Duplicate
                </button>
                <button
                  onClick={handleImport}
                  className="w-full px-3 py-2 text-xs text-left hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                >
                  <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Import JSON
                </button>
                <div className="border-t border-gray-100" />
                <button
                  onClick={handleDelete}
                  className="w-full px-3 py-2 text-xs text-left hover:bg-red-50 text-red-600 cursor-pointer flex items-center gap-2"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete Theme
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar — Editor only, no theme list */}
        <aside className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          <ThemeEditor
            theme={theme}
            prefersDarkMode={prefersDarkMode}
            onUpdate={handleUpdate}
          />
        </aside>

        {/* Preview */}
        <main className="flex-1 overflow-hidden">
          <div className="h-full">
            {previewMode === 'landing' ? (
              <LandingPagePreview theme={theme} isDarkMode={prefersDarkMode} />
            ) : (
              <AppPreview theme={theme} isDarkMode={prefersDarkMode} />
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
